import { z } from "zod";

import { enrichableFieldSchema, prospectScopeSchema } from "./prospect-conditions.schemas.ts";

// The global prospect database: every lead any analysis extracted, across
// platforms and projects. One prospect may be linked to several analyses.

export const platformSchema = z.enum(["instagram", "twitter", "google-maps", "tiktok", "linkedin"]);
export type Platform = z.infer<typeof platformSchema>;

export const qualificationSchema = z.enum([
  "interested",
  "not-interested",
  "wrong-person",
  "do-not-contact",
  "out-of-office",
]);
export type Qualification = z.infer<typeof qualificationSchema>;

export const emailStatusSchema = z.enum(["valid", "invalid", "catch-all", "unknown"]);
export type EmailStatus = z.infer<typeof emailStatusSchema>;

export const genderSchema = z.enum(["male", "female", "unknown"]);
export const entityTypeSchema = z.enum(["person", "business"]);

export const prospectSchema = z.object({
  id: z.string(),
  platform: platformSchema,
  analysisIds: z.array(z.string()),
  name: z.string(),
  username: z.string(),
  /** Filled by AI enrichment; null until a run reveals it. */
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  emailStatus: emailStatusSchema.nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  followers: z.number().int().nonnegative().nullable(),
  following: z.number().int().nonnegative().nullable(),
  biography: z.string().nullable(),
  verified: z.boolean(),
  isBusiness: z.boolean(),
  entityType: entityTypeSchema.nullable(),
  gender: genderSchema.nullable(),
  country: z.string().nullable(),
  language: z.string().nullable(),
  niche: z.string().nullable(),
  b2bB2c: z.enum(["b2b", "b2c", "unknown"]).nullable(),
  /** Month the account was created ("2019-04"). */
  accountAge: z.string().nullable(),
  avgLikes: z.number().nonnegative().nullable(),
  avgComments: z.number().nonnegative().nullable(),
  engagementRate: z.number().nonnegative().nullable(),
  enrichedFields: z.array(enrichableFieldSchema),
  /** Google Maps sources only. */
  category: z.string().nullable(),
  address: z.string().nullable(),
  rating: z.number().min(0).max(5).nullable(),
  reviews: z.number().int().nonnegative().nullable(),
  qualification: qualificationSchema.nullable(),
  doNotContact: z.boolean(),
  createdAt: z.iso.datetime(),
  /** The "lead-scoring" flag's own field (ADR-006 §6, S8.03): present, 0 to 100, only when
   * that flag is on for the caller's organization; absent otherwise, never null. */
  score: z.number().min(0).max(100).optional(),
});
export type Prospect = z.infer<typeof prospectSchema>;

export const listProspectsInputSchema = prospectScopeSchema.extend({
  analysisId: z.string().optional(),
  search: z.string().trim().max(120).optional(),
  /** Free words, any of which a prospect's name, username, biography, niche, country, category
   * or email carries; best match first (D604). */
  words: z.string().trim().max(200).optional(),
  hasEmail: z.boolean().optional(),
  hasPhone: z.boolean().optional(),
  qualification: z.union([qualificationSchema, z.literal("none")]).optional(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().min(1).max(100).default(50),
});
export type ListProspectsInput = z.input<typeof listProspectsInputSchema>;

export const prospectPageSchema = z.object({
  rows: z.array(prospectSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type ProspectPage = z.infer<typeof prospectPageSchema>;

export const getProspectInputSchema = z.object({ prospectId: z.string() });

export const updateProspectsInputSchema = z.object({
  prospectIds: z.array(z.string()).min(1).max(1000),
  qualification: qualificationSchema.nullable().optional(),
  doNotContact: z.boolean().optional(),
});
export type UpdateProspectsInput = z.input<typeof updateProspectsInputSchema>;

export const removeProspectsInputSchema = z.object({ prospectIds: z.array(z.string()).min(1).max(1000) });
export type RemoveProspectsInput = z.infer<typeof removeProspectsInputSchema>;

export const changedCountSchema = z.object({ changed: z.number().int().nonnegative() });
export type ChangedCount = z.infer<typeof changedCountSchema>;

/** What an analysis hands prospect when its run has reached further into the target. */
export type IngestInput = {
  readonly userId: string;
  readonly organizationId: string;
  readonly analysisId: string;
  readonly platform: Platform;
  /** Positions in the target's audience, [from, to). */
  readonly fromIndex: number;
  readonly toIndex: number;
  /** A word describing the audience ("fresh produce", "coffee shops") that colours the generated data. */
  readonly theme: string;
};

export type IngestOutcome = {
  readonly saved: number;
  readonly privateAccounts: number;
  readonly emails: number;
  readonly phones: number;
};

export type ProspectCounts = { readonly total: number; readonly emails: number; readonly phones: number };

export const countMatchingOutputSchema = z.object({
  total: z.number().int().nonnegative(),
  emails: z.number().int().nonnegative(),
  phones: z.number().int().nonnegative(),
});
export type CountMatching = z.infer<typeof countMatchingOutputSchema>;

/** What enrichment asks of prospect: which prospects in a scope still lack the fields. */
export type EnrichmentSelection = {
  readonly prospectIds: readonly string[];
  readonly total: number;
  readonly skipped: number;
};

// S8.03 (ADR-006 §6): a whole door gated by the seeded release flag ("lead-scoring"),
// distinct from `list`'s own field, which the service itself gates (not a whole door).
export const scoreInputSchema = z.object({ prospectId: z.string() });
export type ScoreInput = z.infer<typeof scoreInputSchema>;

export const scoreOutputSchema = z.object({ score: z.number().min(0).max(100) });
export type ScoreOutput = z.infer<typeof scoreOutputSchema>;
